export function getLocalDateInput(date = new Date()) {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function getMonthInputValue(date = new Date()) {
  const value = new Date(date);
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}

export function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function toAbsolute(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) {
    return value;
  }

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${value}`;
}

export function getDefaultPhoto(genre) {
  return genre === "Masculin"
    ? "/img/avatar/avatarM001.webp"
    : "/img/avatar/avatarF001.webp";
}

export function getAdherentPhoto(adherent) {
  return (
    toAbsolute(
      adherent?.photo_url ||
        adherent?.photoURL ||
        adherent?.photo ||
        adherent?.avatar,
    ) || getDefaultPhoto(adherent?.genre)
  );
}

export function getAdherentName(adherent) {
  const nom = String(adherent?.nom || "").trim().toUpperCase();
  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Judoka";
}

export function getGroupName(group) {
  return group?.nom || group?.name || `Groupe #${group?.id || ""}`;
}

export function getGroupAdherents(group) {
  if (Array.isArray(group?.Adherents)) return group.Adherents;
  if (Array.isArray(group?.adherents)) return group.adherents;
  if (Array.isArray(group?.members)) return group.members;

  return [];
}

export function getAdherentGroupStatus(adherent) {
  return (
    adherent?.AdherentGroup?.status ||
    adherent?.adherentGroup?.status ||
    adherent?.adherentgroup?.status ||
    adherent?.status ||
    ""
  );
}

export function getApprovedGroupAdherents(group) {
  const adherents = getGroupAdherents(group);

  return adherents.filter((adherent) => {
    const status = getAdherentGroupStatus(adherent);

    if (!status) return true;

    return status === "approved";
  });
}

export function normalizeGroupAdherentsResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.group?.Adherents)) return response.group.Adherents;
  if (Array.isArray(response?.Group?.Adherents)) return response.Group.Adherents;

  return [];
}

export function normalizeAttendancesResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.attendances)) return response.attendances;
  if (Array.isArray(response?.Attendances)) return response.Attendances;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.items)) return response.items;

  return [];
}

export function getGroupAdherentsCount(group, groupCounts = {}) {
  const groupId = group?.id;

  if (groupId && Object.prototype.hasOwnProperty.call(groupCounts, String(groupId))) {
    return Number(groupCounts[String(groupId)] || 0);
  }

  const directCount =
    group?.nombreAdherents ??
    group?.adherents_count ??
    group?.adherentsCount ??
    group?.members_count ??
    group?.membersCount ??
    group?.countAdherents ??
    null;

  if (directCount !== null && directCount !== undefined && directCount !== "") {
    const numericCount = Number(directCount);

    if (Number.isFinite(numericCount)) {
      return numericCount;
    }
  }

  return getApprovedGroupAdherents(group).length;
}

export function sortAdherentsByName(list = []) {
  return list.slice().sort((a, b) => {
    const byNom = String(a?.nom || "").localeCompare(
      String(b?.nom || ""),
      "fr",
      { sensitivity: "base" },
    );

    if (byNom !== 0) return byNom;

    return String(a?.prenom || "").localeCompare(
      String(b?.prenom || ""),
      "fr",
      { sensitivity: "base" },
    );
  });
}

export function buildDailyAttendanceMap(dateISO, adherentIds = [], attendances = []) {
  const day = String(dateISO || "").slice(0, 10);
  const ids = new Set(adherentIds.map(Number));

  const map = {};

  adherentIds.forEach((id) => {
    map[id] = 0;
  });

  (attendances || []).forEach((attendance) => {
    const attendanceDay = String(attendance?.date_presence || "").slice(0, 10);
    const adherentId = Number(attendance?.adherent_id);

    if (attendanceDay === day && ids.has(adherentId)) {
      map[adherentId] = attendance?.presence ? 1 : 2;
    }
  });

  return map;
}

export function isBirthdayToday(adherent, selectedDate) {
  if (!adherent?.date_naissance || !selectedDate) return false;

  const birth = String(adherent.date_naissance).slice(5, 10);
  const today = String(selectedDate).slice(5, 10);

  return birth === today;
}

export function getAttendanceStatusMeta(status) {
  if (status === 1) {
    return {
      label: "Présent",
      shortLabel: "P",
      overlay: "bg-emerald-500/55",
      badge:
        "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60",
    };
  }

  if (status === 2) {
    return {
      label: "Absent",
      shortLabel: "A",
      overlay: "bg-rose-500/55",
      badge:
        "bg-rose-50 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60",
    };
  }

  return {
    label: "Non saisi",
    shortLabel: "—",
    overlay: "",
    badge:
      "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
  };
}

export function formatDateLongFR(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/* ===== Tableau mensuel ===== */

export function daysArray(yyyyMM) {
  if (!yyyyMM) return [];

  const [year, month] = String(yyyyMM).split("-").map(Number);

  if (!year || !month) return [];

  const count = new Date(year, month, 0).getDate();

  return Array.from({ length: count }, (_, index) =>
    String(index + 1).padStart(2, "0"),
  );
}

export function formatMonthLabel(yyyyMM) {
  if (!yyyyMM) return "—";

  const [year, month] = String(yyyyMM).split("-").map(Number);

  if (!year || !month) return yyyyMM;

  return new Date(year, month - 1, 1).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

export function isAttendancePresentValue(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function isAttendanceAbsentValue(value) {
  return value === false || value === 0 || value === "0" || value === "false";
}

export function buildMonthlyAttendanceRows(
  month,
  adherentsList = [],
  attendancesList = [],
) {
  if (!month || !Array.isArray(adherentsList)) return [];

  const days = daysArray(month);
  const adherentIdSet = new Set(
    adherentsList.map((adherent) => Number(adherent.id)),
  );

  const attendanceMap = {};

  (attendancesList || []).forEach((attendance) => {
    const date = String(attendance?.date_presence || "").slice(0, 10);
    const attendanceMonth = date.slice(0, 7);
    const day = date.slice(8, 10);
    const adherentId = Number(attendance?.adherent_id);

    if (attendanceMonth !== month || !adherentIdSet.has(adherentId)) return;

    if (!attendanceMap[adherentId]) {
      attendanceMap[adherentId] = {};
    }

    if (isAttendancePresentValue(attendance?.presence)) {
      attendanceMap[adherentId][day] = true;
    } else if (isAttendanceAbsentValue(attendance?.presence)) {
      attendanceMap[adherentId][day] = false;
    }
  });

  return sortAdherentsByName(adherentsList).map((adherent) => {
    let totalPresent = 0;
    let totalAbsent = 0;

    const dates = {};

    days.forEach((day) => {
      const value = attendanceMap[Number(adherent.id)]?.[day];

      if (value === true) {
        totalPresent += 1;
      } else if (value === false) {
        totalAbsent += 1;
      }

      dates[day] = value;
    });

    const totalMarked = totalPresent + totalAbsent;
    const rate =
      totalMarked > 0 ? Math.round((totalPresent / totalMarked) * 100) : 0;

    return {
      id: adherent.id,
      nom: adherent.nom || "",
      prenom: adherent.prenom || "",
      adherent,
      dates,
      totalPresent,
      totalAbsent,
      totalMarked,
      rate,
    };
  });
}

export function getMonthlyCellMeta(value) {
  if (value === true) {
    return {
      label: "Présent",
      short: "P",
      className:
        "bg-emerald-500 text-white ring-emerald-500/30 hover:bg-emerald-600",
    };
  }

  if (value === false) {
    return {
      label: "Absent",
      short: "A",
      className: "bg-rose-500 text-white ring-rose-500/30 hover:bg-rose-600",
    };
  }

  return {
    label: "Non saisi",
    short: "",
    className:
      "bg-slate-100 text-slate-400 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-700 dark:hover:bg-slate-700",
  };
}